<template>
  <div class="past-videos-page">
    <header class="past-videos-header">
      <h1 class="past-videos-title">これまでのレッスン動画</h1>
      <p class="past-videos-lead">終わったDayの動画を見返せます（見るだけです）。</p>
      <button type="button" class="past-videos-today" @click="goToToday">今日の練習にもどる</button>
    </header>

    <div v-if="listLoading" class="past-videos-status" role="status" aria-live="polite">読み込み中…</div>
    <div v-else-if="listError" class="past-videos-error" role="alert">
      <p>{{ listError }}</p>
      <button type="button" class="past-videos-retry" @click="loadList">もう一度読み込む</button>
    </div>
    <p v-else-if="!days.length" class="past-videos-status">
      まだ見返せる動画はありません。Day1 を終えると、ここで見返せます。
    </p>
    <ul v-else class="past-videos-list">
      <li v-for="day in days" :key="day">
        <button type="button" class="past-videos-day" @click="openVideo(day)">Day {{ day }}</button>
      </li>
    </ul>

    <!-- 再生（見るだけ）。iPhone で再生できるよう、video 要素は最初から置いておく（タップの中で再生を許可しておく） -->
    <div v-show="playingDay != null" class="past-video-overlay" @click.self="closeVideo">
      <p class="past-video-day">Day {{ playingDay }}</p>
      <p v-if="videoLoading" class="past-video-message">動画を読み込み中…</p>
      <p v-else-if="videoError" class="past-video-message">{{ videoError }}</p>
      <video
        v-show="!videoLoading && !videoError"
        ref="videoEl"
        class="past-video-player"
        controls
        controlsList="nodownload"
        disablePictureInPicture
        playsinline
        preload="metadata"
        @error="onVideoError"
      />
      <button type="button" class="past-video-close" @click="closeVideo">✕ 閉じる</button>
    </div>
  </div>
</template>

<script setup>
/**
 * これまでのレッスン動画（終えたDayの練習動画を見返す。2026-10-25 オーナー決定）
 * - トップの小さなリンク（Day2 から）から開く。終えたDayを新しい順に並べ、押すと動画だけを流す（録音・提出は無い）
 * - 先のDay・今日のDayはサーバーが出さない（/api/past-practice-videos・/api/past-practice-video-url）
 * - 見たあと今日の練習へ戻りやすいよう、上に「今日の練習にもどる」を置く
 */
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { supabase } from '../supabaseClient'
import { getApiBase } from '../utils/apiBase.js'
import { useSelectedLearner } from '../composables/useSelectedLearner.js'

const router = useRouter()
const { selectedLearnerId, loadLearners } = useSelectedLearner()

const days = ref([])
const listLoading = ref(true)
const listError = ref('')
const playingDay = ref(null)
const videoLoading = ref(false)
const videoError = ref('')
const videoEl = ref(null)
// 読み込みの番号（学習者の切り替え・開き直しで、前の遅い応答を使わない）
let listSeq = 0
let videoSeq = 0

const LIST_ERROR_MESSAGE = '動画の一覧を読み込めませんでした。通信状態を確認して、もう一度お試しください。'
const VIDEO_ERROR_MESSAGE = '動画を読み込めませんでした。通信状態を確認して、もう一度お試しください。'

async function getJson(path, params) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { ok: false, status: 401 }
  const base = getApiBase()
  const query = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  const res = await fetch(`${base ? base + '/' : ''}${path}?${query}`, {
    headers: { Authorization: `Bearer ${token}` }
  })
  if (!res.ok) return { ok: false, status: res.status }
  return { ok: true, json: await res.json() }
}

async function loadList() {
  const seq = ++listSeq
  listLoading.value = true
  listError.value = ''
  try {
    await loadLearners()
    const result = await getJson('api/past-practice-videos', { learnerId: selectedLearnerId.value })
    if (seq !== listSeq) return
    if (!result.ok) {
      listError.value = LIST_ERROR_MESSAGE
      days.value = []
      return
    }
    days.value = Array.isArray(result.json.days) ? result.json.days : []
  } catch (e) {
    if (seq !== listSeq) return
    console.error('これまでのレッスン動画の一覧の取得エラー:', e)
    listError.value = LIST_ERROR_MESSAGE
    days.value = []
  } finally {
    if (seq === listSeq) listLoading.value = false
  }
}

async function openVideo(day) {
  const seq = ++videoSeq
  // iPhone は、タップのあとに待ってから初めて再生すると止められるので、タップの中で一度再生を許可しておく
  const el = videoEl.value
  if (el) {
    el.play().catch(() => {})
    el.pause()
  }
  playingDay.value = day
  videoError.value = ''
  videoLoading.value = true
  try {
    const result = await getJson('api/past-practice-video-url', { day, learnerId: selectedLearnerId.value })
    if (seq !== videoSeq) return
    if (!result.ok || !result.json.url) {
      videoError.value = result.status === 403 ? 'このDayの動画は視聴できません。' : VIDEO_ERROR_MESSAGE
      return
    }
    videoLoading.value = false
    if (el) {
      el.src = result.json.url
      el.play().catch(() => {}) // 止められても、再生ボタンで見られる
    }
  } catch (e) {
    if (seq !== videoSeq) return
    console.error('これまでのレッスン動画の取得エラー:', e)
    videoError.value = VIDEO_ERROR_MESSAGE
  } finally {
    if (seq === videoSeq) videoLoading.value = false
  }
}

function onVideoError() {
  if (playingDay.value != null && videoEl.value?.getAttribute('src')) videoError.value = VIDEO_ERROR_MESSAGE
}

function closeVideo() {
  videoSeq += 1
  const el = videoEl.value
  if (el) {
    el.pause()
    el.removeAttribute('src')
    el.load()
  }
  playingDay.value = null
  videoLoading.value = false
  videoError.value = ''
}

function goToToday() {
  closeVideo()
  router.push('/top')
}

// 兄弟の切り替え：その子の一覧に読み直す
watch(selectedLearnerId, (next, prev) => {
  if (next === prev) return
  closeVideo()
  loadList()
})

onMounted(loadList)
onUnmounted(closeVideo)
</script>

<style scoped>
.past-videos-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
}

.past-videos-header {
  width: 100%;
  max-width: 420px;
  text-align: center;
  margin-bottom: 1.5rem;
}

.past-videos-title {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #1f2937;
}

.past-videos-lead {
  margin: 0.5rem 0 1rem;
  font-size: 0.9rem;
  color: #6b7280;
}

.past-videos-today {
  width: 100%;
  padding: 1rem 1.5rem;
  font-size: 1.05rem;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  background: #667eea;
  color: #fff;
  cursor: pointer;
}

.past-videos-today:hover {
  background: #5a67d8;
}

.past-videos-status {
  color: #475569;
  font-size: 0.95rem;
  text-align: center;
}

.past-videos-error {
  width: 100%;
  max-width: 420px;
  padding: 0.75rem 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  text-align: center;
}

.past-videos-error p {
  margin: 0 0 0.5rem;
  line-height: 1.6;
}

.past-videos-retry {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid #fca5a5;
  background: #fff;
  color: #991b1b;
  cursor: pointer;
}

.past-videos-list {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 420px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.6rem;
}

.past-videos-day {
  width: 100%;
  padding: 0.9rem 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #334155;
  cursor: pointer;
}

.past-videos-day:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.past-video-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.85);
}

.past-video-day {
  margin: 0;
  color: #fff;
  font-weight: 700;
}

.past-video-message {
  margin: 0;
  color: #e2e8f0;
  text-align: center;
}

.past-video-player {
  width: 100%;
  max-width: 720px;
  max-height: 70vh;
  background: #000;
  border-radius: 8px;
}

.past-video-close {
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  border: none;
  background: #f1f5f9;
  color: #1f2937;
  font-size: 0.95rem;
  cursor: pointer;
}
</style>
